import React, { ReactNode } from 'react'
import { Button, Popover } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import * as API from '../../../api'
import { StringOperate } from '../../../utils'
import './index.less'

interface FlatTagProps extends TagProps {
	tagName: string;
}

export interface TagValueType {
	content: string;
	[propName: string]: any;
}

export interface TagProps {
	id: number;
	tagKey: string;
	name: string;
	values: TagValueType[];
	direction?: string;
}

export interface TagSelectValueProps {
	[propName: string]: string;
}

export interface TagSelectProps {
	list?: TagProps[]; 	// 支持传入标签分类列表
	visibleNum: number; // 可见标签的数量
	readonly?: boolean; // 是否可操作
	value?: TagSelectValueProps;
	btnText?: string; // 【更多】按钮文案
	onChange?: (value?: TagSelectValueProps) => void;
}

export interface TagSelectState {
	tagList: TagProps[];
	selectTags: TagSelectValueProps;
	popoverVisible: boolean;
	flatTags: FlatTagProps[]; // 扁平化标签数组
	total: number; // 标签总数
}

class TagSelect extends React.Component<TagSelectProps, TagSelectState> {
	static defaultProps = {
		visibleNum: 5,
		readonly: false,
		btnText: '更多',
	}

	state: TagSelectState = {
		tagList: [],
		selectTags: this.props.value || {}, // TODO ?
		popoverVisible: false,
		flatTags: [],
		total: 0,
	}

	componentDidMount() {
		if (this.props.list) { // 传入标签
			this.getTagListFromProps()
		} else { // 从接口获取
			this.getTagList()
		}
	}

	componentDidUpdate(prevProps: TagSelectProps, prevState: TagSelectState) {
		// 如果props变化 更新
		const { list: prevList, value: prevValue } = prevProps
		const { list: currentList, value: currentValue } = this.props
		if (prevList !== currentList || JSON.stringify(prevList) !== JSON.stringify(currentList)) {
			this.getTagListFromProps()
		}
		if (prevValue !== currentValue || JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
			if (!currentValue) return
			this.setState({ selectTags: currentValue })
		}
	}

	async getTagList() {
		const { code, data } = await API.getAllTags()
		if (code === 0) {
			const { readonly, value } = this.props
			const list = readonly && value ? this.getReadOnlySelectedTagList(data) : data
			const flatTags = this.getFlatTags(list)
			this.setState({
				flatTags,
				total: flatTags.length,
				tagList: list,
			})
		}
	}

	/**
	 * 生成只读的已选择标签列表
	 * @param fullTagList 全量标签列表
	 */
	getReadOnlySelectedTagList = (fullTagList: TagProps[]): TagProps[] => {
		const selectTags = this.props.value
		const list: TagProps[] = []
		fullTagList.forEach(item => {
			if (Object.keys(selectTags).includes(item.tagKey)) {
				list.push({
					...item,
					values: item.values.filter(tag => selectTags[item.tagKey].includes(tag.content)),
				})
			}
		})
		return list
	}

	getTagListFromProps() {
		const { list } = this.props
		if (!list) return
		const flatTags = this.getFlatTags(list)
		this.setState({
			flatTags,
			total: flatTags.length,
			tagList: list,
		})
	}

	getFlatTags = (data: TagProps[]) => {
		const flatTags: FlatTagProps[] = []
		data.forEach((cate: TagProps) => {
			const values: TagValueType[] = cate.values
			values.forEach(val => {
				flatTags.push({
					tagName: val.content,
					...cate
				})
			})
		})
		return flatTags
	}

	handlePopoverVisibleChange = (visible: boolean): void => {
		this.setState({
			popoverVisible: visible
		})
	}

	handleTagClick = (tag: FlatTagProps): void => {
		if (this.props.readonly) return

		const { tagKey, tagName } = tag
		const tagsSelected = {}

		if (this.state.selectTags[tagKey]) {
			const data = this.state.selectTags[tagKey]
			tagsSelected[tagKey] = data.includes(tagName) ? StringOperate.remove(data, tagName) : StringOperate.add(data, tagName)
		} else {
			tagsSelected[tagKey] = tagName
		}

		const updateSelectTags = Object.assign({}, this.state.selectTags, tagsSelected)
		this.setState({
			selectTags: updateSelectTags
		})

		// 触发传入的onChange事件
		const { onChange, value } = this.props
		onChange && onChange({ ...value, ...updateSelectTags })
	}

	renderTag = (tag: FlatTagProps, i: number): React.ReactNode => {
		const { tagKey, tagName } = tag
		const cateSelected = this.state.selectTags[tagKey]
		const isActive = cateSelected ? cateSelected.includes(tagName) : false
		return(
			<div
				key={i}
				className={this.props.readonly ? 'content-tag readonly' : isActive ? 'content-tag active' : 'content-tag'}
				onClick={this.handleTagClick.bind(this, tag)}
			>
				{tagName}
			</div>
		)
	}

	renderVisibleTags = (): React.ReactNode => {
		const { flatTags } = this.state
		return flatTags.slice(0, this.props.visibleNum).map((tag, i) => {
			return this.renderTag(tag, i)
		})
	}

	render() {
		const { tagList, popoverVisible, total } = this.state
		const { visibleNum, btnText } = this.props
		return (
			<div className="tag-select clearfix">
				{this.renderVisibleTags()}
				{total > visibleNum && (<Popover
					trigger="click"
					visible={popoverVisible}
					onVisibleChange={this.handlePopoverVisibleChange}
					content={
						<div className="all-tags-popover">
							<CloseOutlined className="icon-close" onClick={this.handlePopoverVisibleChange.bind(this, false)} />
							{tagList.map((tagCate, i) => {
								const { name, values } = tagCate
								return (
									<div className="tag-cate clearfix" key={i}>
										<p className="title">{name}</p>
										{values.map((tag, j) => {
											return this.renderTag({
												tagName: tag.content,
												...tagCate
											}, j)
										})}
									</div>
								)
							})}
						</div>
					}
				>
					<Button type="link" className="more">{btnText}</Button>
				</Popover>)}
			</div>
		)
	}
}

export default TagSelect